import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import {
	ImportMessageResponse,
	PicTimeInvoicesDataResponse,
	PTInvoiceLayout
} from '@models/invoice.model';
import { finalize, takeUntil } from 'rxjs/operators';
import { InvoicesDataProvider } from '@services/invoice/invoices-data-service/invoices.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { InvoiceServiceProvider } from '@services/invoice/invoice-service/invoice.service';
import { BootstrapSettings, GeneralAction } from '@models/models';
import { SettingsProvider } from '@services/settings/settings';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SNModalService } from '@web-services/modal/snModal.service';
import { CopyInvoiceToQuickBooksOrXeroModal } from '@web-components/invoices/modals/copy-invoice-to-qb-or-xero/copy-invoice-to-qb-or-xero.modal';

@Component({
	selector: 'pic-time-invoices-block',
	template: require('./pic-time-invoices-block.component.html'),
	styles: [require('./pic-time-invoices-block.component.scss')]
})
export class PicTimeInvoicesBlockComponent
	implements OnInit, OnChanges, OnDestroy {
	@Input('jobId') jobId: number;
	@Input('picTimeInvoiceLayout') picTimeInvoiceLayout: PTInvoiceLayout;
	@Output() updateJob: EventEmitter<void> = new EventEmitter<void>();

	showLoadingBlock = false;
	integrationSystemName: string = null;
	integrateInvoiceWithOtherSystem = false;
	picTimeInvoicesDataResponse: PicTimeInvoicesDataResponse;
	messagesFromPicTime: string = null;
	getPicTimeInvoicesLink: string;
	availableActions: string[] = [];
	picTimeAvailable: boolean;
	importOrdersDisable = false;
	syncState: boolean;
	copyToAccountingModalRef: NgbModalRef;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private invoiceDataService: InvoicesDataProvider,
		private invoiceServiceProvider: InvoiceServiceProvider,
		private messageService: MessageService,
		private settingsService: SettingsProvider,
		private modalService: SNModalService
	) {}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.isPicTimeAvailable();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!changes.picTimeInvoiceLayout.firstChange) {
			this.isPicTimeAvailable();
		}
	}

	isPicTimeAvailable(): void {
		this.settingsService.timeFormatSettings
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: BootstrapSettings) => {
				if (data.picTimeAvailable) {
					this.getInvoicesPicTime();
				}
			});
	}

	getInvoicesPicTime(): void {
		if (this.picTimeInvoiceLayout.actions.FETCH_PIC_TIME_INVOICES_FOR_JOB) {
			this.picTimeAvailable = true;
			const {
				url
			} = this.picTimeInvoiceLayout.actions.FETCH_PIC_TIME_INVOICES_FOR_JOB;
			this.invoiceDataService
				.getInvoicesPicTime(url)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize((): boolean => (this.showLoadingBlock = false))
				)
				.subscribe(
					(data): void => {
						this.picTimeInvoicesDataResponse = data.data;
						this.picTimeInvoicesDataResponse.invoices = this.invoiceServiceProvider.setFormattedDaysToDue(
							JSON.parse(JSON.stringify(data.data.invoices))
						);
					},
					(error): void => this.messageService.handleError(error)
				);
		} else {
			this.picTimeAvailable = false;
		}
	}

	importPicTimeOrders(): void {
		this.importOrdersDisable = true;
		this.invoiceDataService
			.importInvoicesPicTime(this.jobId, this.integrateInvoiceWithOtherSystem)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.importOrdersDisable = false))
			)
			.subscribe(
				({ data }): void => {
					if (data) {
						const { type, title, message } = data;
						this.messageService.showMessageFromBackend(
							type.toLowerCase().slice(0, 1),
							title,
							message
						);
					}
					this.updateJob.emit();
					this.getInvoicesPicTime();
				},
				(error): void => this.messageService.handleError(error)
			);
	}

	syncPicTimeOrders(action: GeneralAction): void {
		if (action) {
			this.syncState = true;
			const { url, method } = action;
			this.invoiceDataService
				.syncPicTimeOrders(url, method)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize((): boolean => (this.syncState = false))
				)
				.subscribe(
					({ data }): void => {
						this.messageService.showToaster('s_8', 'picTime');
						this.picTimeInvoicesDataResponse = data;
					},
					(error): void => this.messageService.handleError(error)
				);
		}
	}

	approveCopyToAccountingModal(): void {
		if (
			this.picTimeInvoicesDataResponse.ordersFeatureDetails.availableActions.includes(
				'showCopyToXeroModal'
			)
		) {
			this.integrationSystemName = 'Xero';
			this.openCopyToQuickBooksOrXeroModal();
		} else if (
			this.picTimeInvoicesDataResponse.ordersFeatureDetails.availableActions.includes(
				'showCopyToQbModal'
			)
		) {
			this.integrationSystemName = 'QuickBooks';
			this.openCopyToQuickBooksOrXeroModal();
		} else {
			this.importPicTimeOrders();
		}
	}

	openCopyToQuickBooksOrXeroModal(): void {
		this.copyToAccountingModalRef = this.modalService.openModal(
			CopyInvoiceToQuickBooksOrXeroModal,
			{
				windowClass: 'ng-bootstrap-modal-small'
			}
		);
		this.copyToAccountingModalRef.componentInstance.jobId = this.jobId;
		this.copyToAccountingModalRef.componentInstance.integrationSystemName = this.integrationSystemName;
		this.copyToAccountingModalRef.componentInstance.invoiceName = 'picTime';
		this.copyToAccountingModalRef.result.then((): void => {
			this.updateJob.emit();
			this.getInvoicesPicTime();
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/invoices/pic-time-invoices-block/pic-time-invoices-block.component.ts